import React, { useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import Table from "../../components/Table";
import AddDialog from "../../components/Modal/AddModal";
import TextInput from "../../components/Select/InputText";
import SelectInput from "../../components/Select/SelectInput";
import ToggleSwitch from "../../components/Select/ToggleSwitch";
import { useAuth } from "../../context/AuthContext";
import { getCoachesService } from "../../services/coachService";
import { hasPermission } from "../../utils/permissions";

type Coach = {
    _id: string;
    firstName?: string;
    lastName?: string;
    name?: string;
};

type ScheduleSlot = {
    day: string;
    time: string;
};

const StatusCell = ({ value }: { value: boolean }) => (
    <span className={`px-2 py-1 rounded-md ${value ? "text-green-500" : "text-red-500"}`}>
        {value ? "Active" : "Inactive"}
    </span>
);

const formatSchedule = (schedule: ScheduleSlot[]) => {
    if (!schedule.length) return "-";
    return schedule.map((slot) => `${slot.day} ${slot.time}`).join(" • ");
};

const Classes = () => {
    const { user } = useAuth();
    const canEditClasses = hasPermission(user, "classes.write");
    const [addClass, setAddClass] = useState(false);
    const [coaches, setCoaches] = useState<Coach[]>([]);
    const [tableData, setTableData] = useState([
        { _id: "1", name: "Zumba", status: true, coach: "Moe", schedule: [{ day: "Monday", time: "09:00" }] },
        { _id: "2", name: "Taekwondo", status: true, coach: "Moe", schedule: [{ day: "Wednesday", time: "11:00" }] },
        { _id: "3", name: "Crossfit", status: true, coach: "Moe", schedule: [{ day: "Friday", time: "18:00" }] },
    ]);

    useEffect(() => {
        const fetchCoaches = async () => {
            if (!user?.id) return;
            try {
                const result = await getCoachesService();
                setCoaches(result);
            } catch (err) {
                console.error("Failed to fetch coaches", err);
            }
        };
        fetchCoaches();
    }, [user]);

    const coachOptions = useMemo(() => {
        return coaches.map((coach) => {
            const label =
                `${coach.firstName ?? ""} ${coach.lastName ?? ""}`.trim() ||
                coach.name ||
                coach._id;
            return { value: coach._id, label };
        });
    }, [coaches]);

    const dayOptions = [
        { value: "Monday", label: "Monday" },
        { value: "Tuesday", label: "Tuesday" },
        { value: "Wednesday", label: "Wednesday" },
        { value: "Thursday", label: "Thursday" },
        { value: "Friday", label: "Friday" },
        { value: "Saturday", label: "Saturday" },
        { value: "Sunday", label: "Sunday" },
    ];

    const columns = [
        {
            Header: "Name",
            accessor: "name",
        },
        {
            Header: "Coach",
            accessor: "coach",
        },
        {
            Header: "Schedule",
            accessor: "schedule",
            Cell: ({ value }: { value: ScheduleSlot[] }) => formatSchedule(value),
        },
        {
            Header: "Status",
            accessor: "status",
            Cell: StatusCell,
        },
    ];

    const openPopUp = () => setAddClass(true);

    const formik = useFormik({
        initialValues: {
            name: "",
            coachId: "",
            scheduleDay: "",
            scheduleTime: "",
            schedule: [] as ScheduleSlot[],
            status: true,
        },
        validationSchema: Yup.object({
            name: Yup.string().trim().required("Class name is required"),
            coachId: Yup.string().required("Coach is required"),
            schedule: Yup.array()
                .of(
                    Yup.object({
                        day: Yup.string().required(),
                        time: Yup.string().required(),
                    })
                )
                .min(1, "Add at least one class time"),
        }),
        onSubmit: (values, { resetForm }) => {
            const coachLabel =
                coachOptions.find((option) => option.value === values.coachId)?.label ?? "Coach";
            const newClass = {
                _id: crypto.randomUUID(),
                name: values.name,
                coach: coachLabel,
                schedule: values.schedule,
                status: values.status,
            };
            setTableData((prev) => [newClass, ...prev]);
            resetForm();
            setAddClass(false);
        },
    });

    const closePopUp = () => {
        setAddClass(false);
        formik.resetForm();
    };

    return (
        <div className="p-6 w-full">
            <h1 className="text-2xl font-bold mb-4">Classes</h1>
            <Table columns={columns} data={tableData} pageSize={5} add={canEditClasses} onOpen={openPopUp} />

            <AddDialog title="Add New Class" isOpen={addClass} onClose={closePopUp}>
                <form onSubmit={formik.handleSubmit} className="space-y-4">
                    <TextInput
                        label="Class Name"
                        name="name"
                        value={formik.values.name}
                        onChange={formik.handleChange}
                        placeholder="Enter class name"
                    />
                    {formik.touched.name && formik.errors.name && (
                        <p className="text-red-500 text-sm">{formik.errors.name}</p>
                    )}

                    <SelectInput
                        label="Coach"
                        name="coachId"
                        value={formik.values.coachId}
                        onChange={formik.handleChange}
                        options={coachOptions}
                    />
                    {formik.touched.coachId && formik.errors.coachId && (
                        <p className="text-red-500 text-sm">{formik.errors.coachId}</p>
                    )}

                    <div className="space-y-2">
                        <p className="text-sm font-semibold">Class Times</p>
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end">
                            <SelectInput
                                label="Day"
                                name="scheduleDay"
                                value={formik.values.scheduleDay}
                                onChange={formik.handleChange}
                                options={dayOptions}
                            />
                            <TextInput
                                label="Time"
                                type="time"
                                name="scheduleTime"
                                value={formik.values.scheduleTime}
                                onChange={formik.handleChange}
                            />
                            <button
                                type="button"
                                onClick={() => {
                                    if (!formik.values.scheduleDay || !formik.values.scheduleTime) return;
                                    const nextSchedule = [
                                        ...formik.values.schedule,
                                        { day: formik.values.scheduleDay, time: formik.values.scheduleTime },
                                    ];
                                    formik.setFieldValue("schedule", nextSchedule);
                                    formik.setFieldValue("scheduleDay", "");
                                    formik.setFieldValue("scheduleTime", "");
                                }}
                                className="h-10 px-4 rounded-lg bg-gray-800 text-white hover:bg-gray-900"
                            >
                                Add
                            </button>
                        </div>
                        {formik.touched.schedule && formik.errors.schedule && (
                            <p className="text-red-500 text-sm">{formik.errors.schedule as string}</p>
                        )}
                        {formik.values.schedule.length > 0 && (
                            <div className="flex flex-wrap gap-2">
                                {formik.values.schedule.map((slot, index) => (
                                    <span
                                        key={`${slot.day}-${slot.time}-${index}`}
                                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-gray-100 text-sm"
                                    >
                                        {slot.day} {slot.time}
                                        <button
                                            type="button"
                                            onClick={() => {
                                                const nextSchedule = formik.values.schedule.filter(
                                                    (_, i) => i !== index
                                                );
                                                formik.setFieldValue("schedule", nextSchedule);
                                            }}
                                            className="text-gray-500 hover:text-gray-900"
                                        >
                                            ×
                                        </button>
                                    </span>
                                ))}
                            </div>
                        )}
                    </div>

                    <ToggleSwitch
                        label="Active Status"
                        name="status"
                        checked={formik.values.status}
                        onChange={formik.handleChange}
                    />

                    <button
                        type="submit"
                        className="px-4 py-2 bg-[#ac120c] text-white rounded-lg hover:bg-[#8f0f0a]"
                    >
                        Add Class
                    </button>
                </form>
            </AddDialog>
        </div>
    );
};

export default Classes;
